/**
 * Discovery Repository - Platform Persistence layer.
 *
 * Public interface for DiscoveryReport artifact persistence. All reads and
 * writes go through this module. No caller touches Supabase directly for
 * discovery report artifacts.
 *
 * Write path:
 *   writeDiscoveryReport()
 *     → supabase.rpc("write_discovery_report_artifact")  [atomic: version + both INSERTs]
 *     → reads RETURNING (version, created_at)
 *     → assembles ArtifactEnvelope from authoritative database values
 *     → constructArtifact(envelope, content) → DiscoveryReport
 *     → retries up to MAX_VERSION_RETRIES times on UNIQUE constraint violation
 *
 * Read path:
 *   readDiscoveryReportByVersion / readLatestDiscoveryReport / readDiscoveryReportLineage
 *     → joined select on pi_artifact_envelopes + discovery_report_contents
 *     → rowToDiscoveryReport() reassembles typed DiscoveryReport from stored values
 *
 * displayName parameter:
 *   DiscoveryContent carries no display label - it is engine output only.
 *   The caller (orchestration layer) supplies displayName as explicit artifact
 *   metadata. It is stored in the content table for operational purposes but
 *   is not part of the DiscoveryContent type contract.
 *
 * Responsibility boundary:
 *   This module owns: RPC invocation, retry logic, RETURNING assembly,
 *   constructArtifact() call, error translation, public repository interface.
 *   Database layer owns: version arithmetic, timestamp generation, atomicity.
 *   Governance layer owns: artifact identity semantics, type contracts.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ArtifactEnvelope } from "@/types/platformArtifactTypes";
import type { DiscoveryContent, DiscoveryReport } from "@/intelligence/strategicPITypes";
import { constructArtifact } from "@/platform/artifactConstructor";
import { logger } from "@/system/logger";
import { toJson } from "@/integrations/supabase/json";

const MAX_VERSION_RETRIES = 3;
const UNIQUE_VIOLATION_CODE = "23505";

// ─── Private helpers ──────────────────────────────────────────────────────────

type DiscoveryEnvelopeWithContent = {
  version:    number;
  created_at: string;
  discovery_report_contents: {
    display_name:            string;
    inferred_domain:         string;
    domain_confidence:       number;
    inferred_form:           string;
    form_confidence:         number;
    readiness_level:         string;
    analysis_rationale:      string;
    confidence:              number;
    detected_signals:        unknown;
    identified_capabilities: unknown;
    capability_gaps:         unknown;
  } | null;
};

function rowToDiscoveryReport(
  projectId: string,
  row: DiscoveryEnvelopeWithContent,
): DiscoveryReport | null {
  const c = row.discovery_report_contents;
  if (!c) return null;

  const envelope: ArtifactEnvelope = {
    projectId,
    artifactType: "discovery_report",
    version:      row.version,
    createdAt:    row.created_at,
  };

  const content: DiscoveryContent = {
    inferredDomain:         c.inferred_domain        as DiscoveryContent["inferredDomain"],
    domainConfidence:       c.domain_confidence,
    inferredForm:           c.inferred_form           as DiscoveryContent["inferredForm"],
    formConfidence:         c.form_confidence,
    detectedSignals:        c.detected_signals        as DiscoveryContent["detectedSignals"],
    identifiedCapabilities: c.identified_capabilities as DiscoveryContent["identifiedCapabilities"],
    capabilityGaps:         c.capability_gaps         as DiscoveryContent["capabilityGaps"],
    readinessLevel:         c.readiness_level         as DiscoveryContent["readinessLevel"],
    analysisRationale:      c.analysis_rationale,
    confidence:             c.confidence,
  };

  return constructArtifact(envelope, content);
}

// ─── Write ────────────────────────────────────────────────────────────────────

/**
 * Persist a new DiscoveryReport artifact version atomically.
 *
 * The RPC function assigns the next contiguous version via MAX+1 inside the
 * same transaction that inserts both rows. On UNIQUE constraint violation
 * (concurrent writers landing on the same version), this function retries
 * up to MAX_VERSION_RETRIES times before throwing.
 *
 * displayName is stored in the content table for operational labeling.
 * It is not part of DiscoveryContent and is not accessible on the returned
 * DiscoveryReport - it is write-path metadata only.
 */
export async function writeDiscoveryReport(
  projectId:   string,
  displayName: string,
  content:     DiscoveryContent,
): Promise<DiscoveryReport> {
  let attempt = 0;

  while (attempt < MAX_VERSION_RETRIES) {
    const { data, error } = await supabase.rpc("write_discovery_report_artifact", {
      p_project_id:              projectId,
      p_display_name:            displayName,
      p_inferred_domain:         content.inferredDomain,
      p_domain_confidence:       content.domainConfidence,
      p_inferred_form:           content.inferredForm,
      p_form_confidence:         content.formConfidence,
      p_readiness_level:         content.readinessLevel,
      p_analysis_rationale:      content.analysisRationale,
      p_confidence:              content.confidence,
      p_detected_signals:        toJson(content.detectedSignals),
      p_identified_capabilities: toJson(content.identifiedCapabilities),
      p_capability_gaps:         toJson(content.capabilityGaps),
    });

    if (!error) {
      const row = (data as Array<{ version: number; created_at: string }>)[0];
      const envelope: ArtifactEnvelope = {
        projectId,
        artifactType: "discovery_report",
        version:      row.version,
        createdAt:    row.created_at,
      };
      return constructArtifact(envelope, content);
    }

    if ((error as { code?: string }).code === UNIQUE_VIOLATION_CODE) {
      attempt++;
      logger.warn("DiscoveryReport version conflict, retrying", { projectId, attempt });
      continue;
    }

    logger.error("writeDiscoveryReport RPC failed", { projectId, error });
    throw new Error(`writeDiscoveryReport failed: ${error.message}`);
  }

  throw new Error(
    `writeDiscoveryReport exhausted ${MAX_VERSION_RETRIES} retries for project ${projectId}`,
  );
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/**
 * Read a specific DiscoveryReport version. Returns null if the version does
 * not exist for this project.
 */
export async function readDiscoveryReportByVersion(
  projectId: string,
  version:   number,
): Promise<DiscoveryReport | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, discovery_report_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "discovery_report")
    .eq("version", version)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // no rows - version does not exist
    logger.error("readDiscoveryReportByVersion failed", { projectId, version, error });
    throw new Error(`readDiscoveryReportByVersion failed: ${error.message}`);
  }

  return rowToDiscoveryReport(projectId, data as unknown as DiscoveryEnvelopeWithContent);
}

/**
 * Read the latest DiscoveryReport version for a project. Returns null if no
 * discovery reports exist for the project.
 */
export async function readLatestDiscoveryReport(
  projectId: string,
): Promise<DiscoveryReport | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, discovery_report_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "discovery_report")
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    logger.error("readLatestDiscoveryReport failed", { projectId, error });
    throw new Error(`readLatestDiscoveryReport failed: ${error.message}`);
  }

  if (!data) return null;
  return rowToDiscoveryReport(projectId, data as unknown as DiscoveryEnvelopeWithContent);
}

/**
 * Read all DiscoveryReport versions for a project in ascending version order.
 * Returns an empty array if no discovery reports exist for the project.
 */
export async function readDiscoveryReportLineage(
  projectId: string,
): Promise<DiscoveryReport[]> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, discovery_report_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "discovery_report")
    .order("version", { ascending: true });

  if (error) {
    logger.error("readDiscoveryReportLineage failed", { projectId, error });
    throw new Error(`readDiscoveryReportLineage failed: ${error.message}`);
  }

  return (data as unknown as DiscoveryEnvelopeWithContent[])
    .map(row => rowToDiscoveryReport(projectId, row))
    .filter((d): d is DiscoveryReport => d !== null);
}
